import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { companyInfo } from '../../mock';
import Footer from '../../components/Footer';

const DeliveryPolicy = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-dark)' }}>
      {/* Navigation */}
      <nav className="border-b" style={{ borderColor: 'rgba(9, 166, 163, 0.2)', backgroundColor: 'rgba(36, 37, 38, 0.95)' }}>
        <div className="container mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={companyInfo.logo} alt={companyInfo.name} className="h-16 w-auto" />
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <Link to="/" className="inline-flex items-center gap-2 text-accent-teal hover:text-white transition-colors duration-300 mb-8">
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </Link>

        <h1 className="text-4xl md:text-5xl font-bold text-white mb-8">Service Delivery Policy</h1>
        
        <div className="text-neutral-silver leading-relaxed space-y-6">
          <p className="text-lg">Last updated: December 2024</p>

          <p>
            At NT Studios, we provide digital services and products. This Service Delivery Policy outlines how and when our services are delivered to you.
          </p>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">Digital Services - Immediate Access</h2>
            <p className="mb-4">
              Our <strong className="text-accent-teal">AI-powered tools</strong> are delivered digitally and are available immediately upon successful subscription:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Email Generator</strong> - Instant access after signup</li>
              <li><strong>Content Brief Generator</strong> - Available immediately</li>
              <li><strong>Competitor Analysis Tool</strong> - Ready to use right away</li>
            </ul>
            <p className="mt-4">
              Once your payment is processed through Razorpay, your account is automatically upgraded and you can start using premium features immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">Custom Development Services</h2>
            <p className="mb-4">
              For our custom development services (websites, applications, games), delivery timelines vary based on project scope:
            </p>
            
            <h3 className="text-xl font-semibold text-white mb-3 mt-6">Website Development:</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Simple landing pages: 3-5 business days</li>
              <li>Business websites: 2-3 weeks</li>
              <li>Complex web applications: 4-8 weeks</li>
              <li>Timeline provided in project proposal</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">Game Development:</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Educational games: 4-6 weeks</li>
              <li>Custom game projects: 6-12 weeks</li>
              <li>Timeline depends on complexity and requirements</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">Delivery Process</h2>
            <ol className="list-decimal list-inside space-y-3 ml-4">
              <li><strong className="text-white">Project Kickoff:</strong> Initial consultation and requirement gathering</li>
              <li><strong className="text-white">Design Phase:</strong> Mockups and wireframes for approval</li>
              <li><strong className="text-white">Development:</strong> Regular updates and milestone reviews</li>
              <li><strong className="text-white">Testing:</strong> Quality assurance and bug fixes</li>
              <li><strong className="text-white">Delivery:</strong> Final handover with documentation</li>
              <li><strong className="text-white">Support:</strong> Post-delivery support period</li>
            </ol>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">Communication During Development</h2>
            <p className="mb-4">We maintain transparent communication throughout the project:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Weekly progress updates via email</li>
              <li>Milestone reviews with the client</li>
              <li>Available for queries during business hours</li>
              <li>Dedicated project manager for enterprise clients</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">Revisions and Changes</h2>
            <p className="mb-4">
              All projects include a specified number of revision rounds:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Standard projects: 2-3 revision rounds</li>
              <li>Premium projects: Unlimited revisions within scope</li>
              <li>Additional revisions beyond agreed scope may incur extra charges</li>
              <li>Major scope changes will be quoted separately</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">Delays and Extensions</h2>
            <p className="mb-4">
              While we strive to meet all deadlines, delays may occur due to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Delayed feedback or approvals from client</li>
              <li>Changes in project scope</li>
              <li>Technical challenges requiring additional time</li>
              <li>Force majeure events</li>
            </ul>
            <p className="mt-4">
              In case of delays, we will notify you promptly and provide a revised timeline.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">Post-Delivery Support</h2>
            <p className="mb-4">All delivered projects include:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>30-day bug fix guarantee</li>
              <li>Documentation and training materials</li>
              <li>Technical support for 30 days post-delivery</li>
              <li>Extended support packages available</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">Access and Credentials</h2>
            <p>
              For digital products and services:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
              <li>Login credentials sent via registered email</li>
              <li>Access activated within 5 minutes of payment confirmation</li>
              <li>Source code and files delivered via secure cloud storage</li>
              <li>All deliverables include proper documentation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">No Physical Shipping</h2>
            <p>
              NT Studios provides digital services only. We do not ship physical products. All deliverables are provided digitally via:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
              <li>Email attachments</li>
              <li>Cloud storage links (Google Drive, Dropbox)</li>
              <li>Direct access to online platforms</li>
              <li>GitHub repositories for code projects</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">Contact Us</h2>
            <p>
              For questions about service delivery:
            </p>
            <ul className="list-none space-y-2 mt-4">
              <li>Email: <a href="mailto:support@ntstudios.com" className="text-accent-teal hover:underline">support@ntstudios.com</a></li>
              <li>Contact Form: <Link to="/#contact" className="text-accent-teal hover:underline">ntstudios.com/#contact</Link></li>
            </ul>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DeliveryPolicy;
